import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './App.css';
import LoginScreen from './LoginScreen';
import logo from './img/logo.png';

const API_BASE_URL = '/api'; // URL relativa para funcionar em qualquer IP

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [unidades, setUnidades] = useState([]);
  const [selectedUnidade, setSelectedUnidade] = useState('');
  const [currentCall, setCurrentCall] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showUnitSelector, setShowUnitSelector] = useState(true);
  const [speechEnabled, setSpeechEnabled] = useState(true);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [currentUser, setCurrentUser] = useState(null);
  const [videoConfigList, setVideoConfigList] = useState([]);
  const [showVideoConfig, setShowVideoConfig] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoDescription, setNewVideoDescription] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Volume do vídeo (0-100), salvo no navegador para manter após recarregar
  const [videoVolume, setVideoVolume] = useState(() => {
    const saved = parseInt(localStorage.getItem('painelVideoVolume'), 10);
    return Number.isNaN(saved) ? 30 : saved;
  });
  // Ref usada pelas funções de áudio, que rodam em closures antigas (polling)
  const videoVolumeRef = useRef(videoVolume);
  // Indica se uma chamada está tocando (vídeo deve permanecer mudo)
  const isCallingRef = useRef(false);

  // Verificar autenticação na inicialização
  useEffect(() => {
    checkAuthentication();
    
    // Verificar se há unidade na URL
    const params = new URLSearchParams(window.location.search);
    const unidadeUrl = params.get('unidade');
    if (unidadeUrl) {
      console.log('🔗 Unidade encontrada na URL:', unidadeUrl);
      setSelectedUnidade(unidadeUrl);
    }
  }, []);

  // Carregar vídeos quando autenticar
  useEffect(() => {
    if (isAuthenticated) {
      loadVideoConfig();
    }
  }, [isAuthenticated]);

  // Detectar mudanças de tela cheia
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Atualizar relógio a cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Listar vozes disponíveis no console
  useEffect(() => {
    const listVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        console.log('🎵 Vozes disponíveis:');
        voices.forEach((voice, index) => {
          console.log(`${index}: ${voice.name} (${voice.lang}) - ${voice.gender || 'N/A'}`);
        });
      }
    };

    // Aguardar as vozes carregarem
    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.addEventListener('voiceschanged', listVoices);
    } else {
      listVoices();
    }

    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', listVoices);
    };
  }, []);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('painelAuthToken');
    if (token) {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setIsAuthenticated(true);
          setCurrentUser(response.data.data.user);
        } else {
          localStorage.removeItem('painelAuthToken');
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Token inválido:', error);
        localStorage.removeItem('painelAuthToken');
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    } else {
      setIsAuthenticated(false);
      setCurrentUser(null);
    }
    setCheckingAuth(false);
  };

  const handleLogin = (userData) => {
    localStorage.setItem('painelAuthToken', userData.token);
    setIsAuthenticated(true);
    setCurrentUser(userData.user);
    // Carregar vídeos imediatamente após login
    setTimeout(() => loadVideoConfig(), 100);
  };

  const handleLogout = () => {
    localStorage.removeItem('painelAuthToken');
    setIsAuthenticated(false);
    setCurrentUser(null);
    // Limpar dados do painel
    setSelectedUnidade('');
    setCurrentCall(null);
    setHistorico([]);
    setTimer(0);
    setIsTimerActive(false);
    setShowUnitSelector(true);
    
    // Limpar parâmetro da URL ao sair
    const url = new URL(window.location);
    url.searchParams.delete('unidade');
    window.history.pushState({}, '', url);
  };

  const getUserDisplayName = (user) => {
    if (!user) return '';
    const roleNames = {
      administrator: 'Administrador',
      operator: 'Operador',
      user: 'Usuário'
    };
    return `${user.username} (${roleNames[user.role] || user.role})`;
  };

  // Função para extrair ID do YouTube de uma URL
  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Carregar configuração de vídeos
  const loadVideoConfig = async () => {
    const token = localStorage.getItem('painelAuthToken');
    if (!token) {
      console.log('⚠️ Sem token para carregar vídeos');
      return;
    }

    try {
      console.log('🎥 Carregando configuração de vídeos...');
      const response = await axios.get(`${API_BASE_URL}/video-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setVideoConfigList(response.data.data || []);
        console.log(`✅ ${response.data.data.length} vídeos carregados`);
      } else {
        setVideoConfigList([]);
        console.log('⚠️ Nenhum vídeo encontrado');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar configuração de vídeos:', error);
      setVideoConfigList([]);
    }
  };

  // Adicionar novo vídeo
  const addVideo = async () => {
    if (!newVideoUrl.trim()) {
      alert('Por favor, insira a URL do vídeo.');
      return;
    }
    
    if (!newVideoDescription.trim()) {
      alert('Por favor, insira a descrição do vídeo.');
      return;
    }
    
    const videoId = extractYouTubeId(newVideoUrl);
    if (!videoId) {
      alert('URL do YouTube inválida. Use um link como: https://www.youtube.com/watch?v=...');
      return;
    }

    const token = localStorage.getItem('painelAuthToken');
    if (!token) {
      alert('Sessão expirada. Faça login novamente.');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/video-config`, {
        url: newVideoUrl,
        videoId: videoId,
        description: newVideoDescription.trim()
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setNewVideoUrl('');
        setNewVideoDescription('');
        alert('Vídeo adicionado com sucesso!');
        loadVideoConfig(); // Recarregar lista
      } else {
        alert('Erro ao salvar vídeo: ' + response.data.error);
      }
    } catch (error) {
      console.error('Erro ao adicionar vídeo:', error);
      alert('Erro de conexão ao salvar vídeo.');
    }
  };

  // Remover vídeo
  const removeVideo = async (videoId) => {
    const token = localStorage.getItem('painelAuthToken');
    if (!token) {
      alert('Sessão expirada. Faça login novamente.');
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/video-config/${videoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        alert('Vídeo removido com sucesso!');
        loadVideoConfig(); // Recarregar lista
      } else {
        alert('Erro ao remover vídeo: ' + response.data.error);
      }
    } catch (error) {
      console.error('Erro ao remover vídeo:', error);
      alert('Erro de conexão ao remover vídeo.');
    }
  };

  // Obter playlist de todos os vídeos para loop contínuo
  const getVideoPlaylist = () => {
    if (videoConfigList.length === 0) return null;
    
    // Criar string com todos os IDs dos vídeos para a playlist
    const videoIds = videoConfigList.map(video => video.video_id);
    const playlistString = videoIds.join(',');
    
    return {
      firstVideoId: videoIds[0],
      playlistIds: playlistString,
      totalVideos: videoConfigList.length
    };
  };

  // Função para tocar beep estilo teclas (nítido e chamativo)
  const playBeep = () => {
    try {
      // Criar contexto de áudio Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // === PRIMEIRO BEEP - Tom agudo estilo tecla DTMF ===
      const oscillator1 = audioContext.createOscillator(); // Gerador de frequência
      const gainNode1 = audioContext.createGain(); // Controlador de volume
      
      // Conectar oscilador -> controle de volume -> alto-falantes
      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);
      
      oscillator1.type = 'square'; // Onda quadrada = som eletrônico nítido (vs senoidal suave)
      oscillator1.frequency.setValueAtTime(1209, audioContext.currentTime); // 1209Hz = frequência da tecla "1" do telefone
      
      // Envelope ADSR para som de tecla bem definido
      gainNode1.gain.setValueAtTime(0, audioContext.currentTime); // Começar mudo
      gainNode1.gain.linearRampToValueAtTime(0.8, audioContext.currentTime + 0.01); // Ataque instantâneo (10ms) = som "seco"
      gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15); // Decay rápido (150ms) = som cortado
      
      oscillator1.start(audioContext.currentTime); // Iniciar som imediatamente
      oscillator1.stop(audioContext.currentTime + 0.15); // Parar após 150ms
      
      // === SEGUNDO BEEP - Tom mais grave para contraste melódico ===
      const oscillator2 = audioContext.createOscillator(); // Segundo gerador de som
      const gainNode2 = audioContext.createGain(); // Segundo controlador de volume
      
      // Conectar segundo oscilador -> volume -> alto-falantes
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      
      oscillator2.type = 'square'; // Mesmo tipo de onda = consistência sonora
      oscillator2.frequency.setValueAtTime(770, audioContext.currentTime + 0.2); // 770Hz = frequência mais grave (tecla "4"), inicia 200ms depois
      
      // Envelope idêntico para consistência
      gainNode2.gain.setValueAtTime(0, audioContext.currentTime + 0.2); // Começar mudo aos 200ms
      gainNode2.gain.linearRampToValueAtTime(0.8, audioContext.currentTime + 0.21); // Ataque rápido (10ms)
      gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.35); // Decay até 350ms (duração 150ms)
      
      oscillator2.start(audioContext.currentTime + 0.2); // Iniciar aos 200ms (pausa de 50ms entre beeps)
      oscillator2.stop(audioContext.currentTime + 0.35); // Terminar aos 350ms
      
      // Total: Beep1 (150ms) + Pausa (50ms) + Beep2 (150ms) = 350ms de duração total
      console.log('📞 Beep estilo tecla DTMF tocado - Duração total: 350ms');
    } catch (error) {
      console.error('Erro ao tocar beep:', error);
    }
  };

  // Aplicar o volume configurado no player do YouTube
  const applyVideoVolume = (volume) => {
    const videoFrame = document.querySelector('iframe');
    if (videoFrame) {
      try {
        if (volume <= 0) {
          videoFrame.contentWindow.postMessage('{"event":"command","func":"mute","args":[]}', '*');
        } else {
          videoFrame.contentWindow.postMessage('{"event":"command","func":"unMute","args":[]}', '*');
        }
        videoFrame.contentWindow.postMessage(`{"event":"command","func":"setVolume","args":[${volume}]}`, '*');
      } catch (error) {
        console.log('Não foi possível ajustar o volume do vídeo:', error);
      }
    }
  };

  // Atualizar volume ao mover o controle (sem interferir em chamada em andamento)
  useEffect(() => {
    videoVolumeRef.current = videoVolume;
    localStorage.setItem('painelVideoVolume', String(videoVolume));
    if (!isCallingRef.current) {
      applyVideoVolume(videoVolume);
    }
  }, [videoVolume]);

  // Quando o iframe carrega, o player ainda não está pronto: reaplicar o volume algumas vezes
  const handleVideoLoad = () => {
    [1000, 3000, 6000].forEach((delay) => {
      setTimeout(() => {
        if (!isCallingRef.current) {
          applyVideoVolume(videoVolumeRef.current);
        }
      }, delay);
    });
  };

  // Função para restaurar volume do vídeo
  const restoreVideoVolume = () => {
    isCallingRef.current = false;
    const videoFrame = document.querySelector('iframe');
    if (videoFrame) {
      try {
        videoFrame.contentWindow.postMessage('{"event":"command","func":"playVideo","args":[]}', '*');
        applyVideoVolume(videoVolumeRef.current);
        console.log('🔊 Volume do vídeo restaurado e reprodução continuada');
      } catch (error) {
        console.log('Não foi possível restaurar o volume do vídeo:', error);
      }
    }
  };

  // Função para mutar vídeo (SEM pausar)
  const muteVideo = () => {
    isCallingRef.current = true;
    const videoFrame = document.querySelector('iframe');
    if (videoFrame) {
      try {
        // APENAS mutar, NÃO pausar
        videoFrame.contentWindow.postMessage('{"event":"command","func":"mute","args":[]}', '*');
        videoFrame.contentWindow.postMessage('{"event":"command","func":"setVolume","args":[0]}', '*');
        console.log('🔇 Vídeo mutado (continua tocando)');
      } catch (error) {
        console.log('Não foi possível mutar o vídeo:', error);
      }
    }
  };

  // Função para falar a mensagem usando áudio do servidor
  const speakMessage = async (chamada) => {
    console.log('🎤 speakMessage chamado - speechEnabled:', speechEnabled);
    
    if (!speechEnabled) {
      console.log('🔇 Áudio desabilitado, não falando');
      return;
    }
    
    // Mutar o vídeo completamente durante a chamada
    muteVideo();
    
    // Tocar beep antes de falar
    playBeep();
    
    try {
      // Criar elemento de áudio para tocar o MP3 do servidor
      const audio = new Audio(`${API_BASE_URL}/audio/${selectedUnidade}/${chamada.co_seq_atend}`);
      audio.volume = 1.0;
      
      console.log('🎵 Carregando áudio do servidor:', `${API_BASE_URL}/audio/${selectedUnidade}/${chamada.co_seq_atend}`);
      
      // Timer de segurança para restaurar volume (15 segundos no máximo)
      const volumeRestoreTimer = setTimeout(() => {
        restoreVideoVolume();
        console.log('⏰ Volume restaurado via timer de segurança');
      }, 15000);
      
      // Quando o áudio terminar de tocar
      audio.addEventListener('ended', () => {
        clearTimeout(volumeRestoreTimer);
        restoreVideoVolume();
        console.log('✅ Áudio do servidor finalizado, volume restaurado');
      });
      
      // Em caso de erro, tentar fallback para Web Speech API
      audio.addEventListener('error', (e) => {
        clearTimeout(volumeRestoreTimer);
        console.error('❌ Erro ao carregar áudio do servidor:', e);
        console.log('🔄 Tentando fallback com Web Speech API...');
        
        // Fallback: usar Web Speech API
        const message = generateCallMessage(chamada, chamada.co_status_atend);
        const utterance = new SpeechSynthesisUtterance(message);
        
        const voices = window.speechSynthesis.getVoices();
        const selectedVoice = voices.find(voice => 
          voice.lang === 'pt-BR' || voice.lang === 'pt'
        ) || voices[0];
        
        if (selectedVoice) {
          utterance.voice = selectedVoice;
        }
        
        utterance.lang = 'pt-BR';
        utterance.rate = 0.8;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        
        utterance.addEventListener('end', () => {
          restoreVideoVolume();
          console.log('🎵 Fallback finalizado, volume restaurado');
        });
        
        window.speechSynthesis.speak(utterance);
      });
      
      // Aguardar beep completar antes de tocar áudio (800ms)
      setTimeout(() => {
        audio.play().catch(err => {
          console.error('Erro ao tocar áudio:', err);
          clearTimeout(volumeRestoreTimer);
          restoreVideoVolume();
        });
      }, 800);
      
    } catch (error) {
      console.error('Erro ao processar áudio:', error);
      restoreVideoVolume();
    }
  };

  // Função para gerar mensagem baseada no status
  const generateCallMessage = (paciente, status) => {
    const nome = paciente.no_cidadao;
    
    switch (status) {
      case 2:
        return `${nome}, comparecer para realização de escuta inicial`;
      case 3:
        return `${nome}, comparecer ao consultório para atendimento`;
      default:
        return `${nome}, comparecer ao consultório`;
    }
  };

  // Carregar unidades na inicialização
  useEffect(() => {
    loadUnidades();
  }, []);

  // Carregar chamadas quando uma unidade for selecionada
  useEffect(() => {
    if (selectedUnidade) {
      loadChamadaAtual();
      loadHistorico();
    }
  }, [selectedUnidade]);

  // Timer de 7 segundos para chamadas
  useEffect(() => {
    let interval = null;
    
    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
    } else if (timer === 0 && isTimerActive && currentCall) {
      // Timer acabou, marcar como processado
      processarChamada();
      setIsTimerActive(false);
    }
    
    return () => clearInterval(interval);
  }, [timer, isTimerActive, currentCall]);

  // Polling para verificar novas chamadas
  useEffect(() => {
    if (selectedUnidade && !isTimerActive) {
      const interval = setInterval(() => {
        loadChamadaAtual();
      }, 7000); // Verifica a cada 7 segundos

      return () => clearInterval(interval);
    }
  }, [selectedUnidade, isTimerActive]);

  const loadUnidades = async () => {
    try {
      console.log('🔍 Carregando unidades...');
      const response = await axios.get(`${API_BASE_URL}/unidades`);
      console.log('📋 Resposta da API:', response.data);
      
      if (response.data.success) {
        setUnidades(response.data.data);
        console.log(`✅ ${response.data.data.length} unidades carregadas`);
      } else {
        console.error('❌ API retornou erro:', response.data.error);
        alert('Erro ao carregar unidades: ' + response.data.error);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar unidades:', error);
      alert('Erro de conexão ao carregar unidades. Verifique se a API está rodando na porta 3001.');
    }
  };

  const loadChamadaAtual = useCallback(async () => {
    if (!selectedUnidade) return;

    try {
      // Só mostrar loading se não há chamada atual (primeira busca)
      if (!currentCall) {
        setLoading(true);
      }
      
      const response = await axios.get(`${API_BASE_URL}/chamadas/${selectedUnidade}`);
      
      if (response.data.success && response.data.data) {
        const novaChamada = response.data.data;
        
        // Marcar como processado IMEDIATAMENTE para não chamar novamente
        await processarChamadaImediato(novaChamada.co_seq_atend);
        
        // Exibir a chamada
        setCurrentCall(novaChamada);
        setTimer(7);
        setIsTimerActive(true);
        loadHistorico();
        
        // Falar a mensagem usando áudio do servidor
        speakMessage(novaChamada);
        
        console.log('🔔 Chamando:', novaChamada.no_cidadao, '- Status:', novaChamada.co_status_atend);
        console.log('✅ Chamada marcada como processada imediatamente');
      } else {
        console.log('🔄 Nenhuma chamada pendente');
      }
    } catch (error) {
      console.error('Erro ao carregar chamada atual:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedUnidade, speechEnabled, currentCall]);

  const loadHistorico = async () => {
    if (!selectedUnidade) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/chamadas/${selectedUnidade}/historico`);
      if (response.data.success) {
        setHistorico(response.data.data || []); // Garantir array vazio se não houver dados
      } else {
        setHistorico([]); // Limpar histórico se não houver sucesso na resposta
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setHistorico([]); // Limpar histórico em caso de erro
    }
  };

  const processarChamada = async () => {
    if (!currentCall || !selectedUnidade) return;

    try {
      await axios.put(`${API_BASE_URL}/chamadas/${selectedUnidade}/${currentCall.co_seq_atend}/processar`);
      
      console.log('✅ Chamada processada, mantendo na tela até próxima');
      
      // NÃO limpar a chamada atual - manter na tela
      // Apenas parar o timer e atualizar histórico
      setTimer(0);
      setIsTimerActive(false);
      
      // Atualizar histórico
      loadHistorico();
      
    } catch (error) {
      console.error('Erro ao processar chamada:', error);
    }
  };

  const processarChamadaImediato = async (coSeqAtend) => {
    if (!selectedUnidade) return;

    try {
      await axios.put(`${API_BASE_URL}/chamadas/${selectedUnidade}/${coSeqAtend}/processar`);
      console.log('✅ Chamada marcada como processada imediatamente:', coSeqAtend);
    } catch (error) {
      console.error('Erro ao processar chamada imediatamente:', error);
      throw error; // Re-throw para que loadChamadaAtual saiba que houve erro
    }
  };

  const formatTime = (seconds) => {
    return `${seconds}s`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatCurrentDateTime = (date) => {
    const dateStr = date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    return `${dateStr} - ${timeStr}`;
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case 2:
        return 'TRIAGEM';
      case 3:
        return 'ATENDIMENTO';
      default:
        return 'ATENDIMENTO';
    }
  };

  const getRiskClassificationColor = (classificacao) => {
    if (!classificacao || classificacao === '-') {
      return '#f7fafc'; // Cinza muito claro para sem classificação
    }
    
    const risco = classificacao.toUpperCase();
    
    switch (risco) {
      case 'NÃO CLASSIFICADO':
        return '#e2e8f0'; // Cinza mais visível
      case 'NÃO URGENTE':
        return '#9ae6b4'; // Verde mais vivo
      case 'POUCO URGENTE':
        return '#68d391'; // Verde forte
      case 'BAIXA':
        return '#63b3ed'; // Azul mais vivo
      case 'INTERMEDIÁRIA':
        return '#f6e05e'; // Amarelo vibrante
      case 'URGENTE':
        return '#f687b3'; // Rosa mais forte
      case 'ALTA':
        return '#ed8936'; // Laranja vibrante
      case 'MUITO URGENTE':
        return '#f56565'; // Vermelho vibrante
      case 'EMERGÊNCIA':
        return '#e53e3e'; // Vermelho intenso
      default:
        return '#e2e8f0'; // Cinza padrão
    }
  };

  const selectedUnidadeData = unidades.find(u => u.nu_cnes === selectedUnidade);

  // Mostrar loading enquanto verifica autenticação
  if (checkingAuth) {
    return (
      <div className="app" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="loading">
          <div className="spinner"></div>
          <span>Verificando autenticação...</span>
        </div>
      </div>
    );
  }

  // Mostrar tela de login se não estiver autenticado
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      {/* Título fixo quando header oculto */}
      {!showUnitSelector && (
        <div className="fixed-title">
          <div className="logo">
            <img src={logo} alt="Logo" className="logo-icon" />
            <h1>Painel de Chamadas</h1>
          </div>
          <div className="header-info">
            <div className="datetime-display">
              {formatCurrentDateTime(currentDateTime)}
            </div>
            {selectedUnidadeData && (
              <div className="unit-name">
                {selectedUnidadeData.no_unidade_saude}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      {showUnitSelector && (
        <header className="header">
          <div className="header-content">
            <div className="logo">
              <img src={logo} alt="Logo" className="logo-icon" />
              <h1>Painel de Chamadas</h1>
            </div>
            
              <div className="header-controls">
                <div className="user-info">
                  <span className="user-display">
                    👤
                    <span className="user-tooltip">
                      {getUserDisplayName(currentUser)}
                    </span>
                  </span>
                </div>

                {currentUser && currentUser.role === 'administrator' && (
                  <button
                    className="config-btn"
                    onClick={() => setShowVideoConfig(!showVideoConfig)}
                    title="Configurar vídeos"
                  >
                    🎥
                  </button>
                )}

                <div className="audio-control">
                  <button
                    className={`audio-btn ${speechEnabled ? 'enabled' : 'disabled'}`}
                    onClick={() => {
                      const newState = !speechEnabled;
                      console.log('🎵 Botão áudio clicado - novo estado:', newState);
                      setSpeechEnabled(newState);
                      
                      // Se estiver desabilitando, cancelar qualquer fala em andamento
                      if (!newState) {
                        console.log('🔇 Cancelando fala em andamento');
                        window.speechSynthesis.cancel();
                      }
                    }}
                    title={speechEnabled ? "Desabilitar áudio" : "Habilitar áudio"}
                  >
                    {speechEnabled ? '🔊' : '🔇'}
                  </button>
                </div>
                
                {selectedUnidade && (
                  <button
                    className="change-unit-btn"
                    onClick={() => {
                      setSelectedUnidade('');
                      setCurrentCall(null);
                      setTimer(0);
                      setIsTimerActive(false);
                      setHistorico([]);
                      setShowUnitSelector(true); // Garante que o menu esteja visível ao voltar
                      
                      // Limpar parâmetro da URL ao trocar unidade
                      const url = new URL(window.location);
                      url.searchParams.delete('unidade');
                      window.history.pushState({}, '', url);
                    }}
                    title="Trocar Unidade"
                  >
                    🏥 Trocar Unidade
                  </button>
                )}
                
                <button
                  className="logout-btn"
                  onClick={handleLogout}
                  title="Sair do sistema"
                >
                  Sair
                </button>
            </div>
          </div>
          
          {/* Painel de configuração de vídeos */}
          {showVideoConfig && currentUser && currentUser.role === 'administrator' && (
            <div className="video-config-panel">
              <h3>Configuração de Vídeos do YouTube</h3>
              
              <div className="video-config-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>URL do YouTube:</label>
                    <input
                      type="text"
                      value={newVideoUrl}
                      onChange={(e) => setNewVideoUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Descrição do vídeo:</label>
                    <input
                      type="text"
                      value={newVideoDescription}
                      onChange={(e) => setNewVideoDescription(e.target.value)}
                      placeholder="Ex: Vídeo sobre prevenção..."
                    />
                  </div>
                  
                  <button onClick={addVideo} className="add-video-btn">
                    Adicionar
                  </button>
                </div>
              </div>

              <div className="video-list">
                <h4>Vídeos Configurados ({videoConfigList.length}):</h4>
                {videoConfigList.length === 0 ? (
                  <p>Nenhum vídeo configurado</p>
                ) : (
                  <div className="video-items">
                    {videoConfigList.map((video, index) => (
                      <div key={video.id} className="video-item">
                        <div className="video-info">
                          <strong>#{index + 1}: {video.description}</strong>
                          <small>{video.url}</small>
                        </div>
                        <button
                          onClick={() => removeVideo(video.id)}
                          className="remove-video-btn"
                          title="Remover vídeo"
                        >
                          ❌
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </header>
      )}

      {/* Botões flutuantes */}
      <div className="floating-buttons-container">
        <button
          className="floating-btn"
          onClick={() => {
            if (isFullscreen) {
              document.exitFullscreen();
            } else {
              document.documentElement.requestFullscreen();
            }
          }}
          title="Alternar tela cheia"
        >
          {isFullscreen ? '🔲' : '🔳'}
        </button>
        
        <button
          className="floating-btn"
          onClick={() => setShowUnitSelector(!showUnitSelector)}
          title={showUnitSelector ? "Ocultar menu" : "Mostrar menu"}
        >
          {showUnitSelector ? '➖' : '➕'}
        </button>
      </div>

      {/* Main Content */}
      <main className={`main-content ${!showUnitSelector ? 'full-height' : ''}`}>
        {!selectedUnidade ? (
          <div className="unit-selection-screen">
            <div className="unit-selection-container">
              <h2>Selecione uma Unidade de Saúde</h2>
              <p>Escolha a unidade abaixo para visualizar o painel de chamadas</p>
              
              <div className="unit-grid">
                {unidades.map((unidade) => (
                  <button 
                    key={unidade.nu_cnes} 
                    className="unit-card"
                    onClick={() => {
                      setSelectedUnidade(unidade.nu_cnes);
                      setCurrentCall(null);
                      setTimer(0);
                      setIsTimerActive(false);
                      setHistorico([]);
                      
                      // Atualizar URL com a unidade selecionada
                      const url = new URL(window.location);
                      url.searchParams.set('unidade', unidade.nu_cnes);
                      window.history.pushState({}, '', url);
                    }}
                  >
                    <div className="unit-icon">🏥</div>
                    <div className="unit-info">
                      <span className="unit-cnes">CNES: {unidade.nu_cnes}</span>
                      <span className="unit-name-text">{unidade.no_unidade_saude}</span>
                    </div>
                  </button>
                ))}
              </div>
              
              {unidades.length === 0 && !loading && (
                <div className="no-units">
                  <p>Nenhuma unidade encontrada.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="dashboard">
            {/* Painel Principal */}
            <div className="main-panel">
              {loading ? (
                <div className="loading">
                  <div className="spinner"></div>
                  <span>Carregando...</span>
                </div>
              ) : currentCall ? (
                <>
                  {isTimerActive && (
                    <div className="timer-display">
                      {formatTime(timer)}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }}>
                    <div 
                      className={`calling-status ${!isTimerActive ? 'processed' : ''}`}
                      style={{ border: '2px solid #000000' }}
                    >
                      {isTimerActive ? 'ATENÇÃO' : 'ÚLTIMO CHAMADO'}
                    </div>
                    
                    {currentCall.classificacao_risco && currentCall.classificacao_risco !== '-' && (
                      <div 
                        className="calling-status-static"
                        style={{
                          backgroundColor: `${getRiskClassificationColor(currentCall.classificacao_risco)} !important`,
                          background: getRiskClassificationColor(currentCall.classificacao_risco),
                          border: '2px solid #000000ff',
                          color: '#000000ff',
                          fontWeight: 'bold'
                        }}
                      >
                        CLASSIFICAÇÃO: {currentCall.classificacao_risco}
                      </div>
                    )}
                  </div>
                  
                  <div className="patient-name">
                    {currentCall.no_cidadao}
                  </div>
                  
                  <div className="patient-details">
                    <div className="detail-item">
                      <div className="label">LOCAL</div>
                      <div className="value">{currentCall.servico}</div>
                    </div>
                    
                    <div className="detail-item">
                      <div className="label">Profissional</div>
                      <div className="value">{currentCall.no_profissional || '-'}</div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-calls">
                  <p>Aguardando próxima chamada...</p>
                  {selectedUnidadeData && (
                    <p style={{ fontSize: '1rem', marginTop: '1rem', color: '#a0aec0' }}>
                      {selectedUnidadeData.no_unidade_saude}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Painel de Vídeo */}
            <div className="video-panel">
              <div className="video-panel-header">
                <h3>🎞️ Mídia Informativa</h3>
                <div className="video-volume-control" title="Volume do vídeo">
                  <span>{videoVolume === 0 ? '🔇' : '🔉'}</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={videoVolume}
                    onChange={(e) => setVideoVolume(parseInt(e.target.value, 10))}
                    aria-label="Volume do vídeo"
                  />
                  <span className="video-volume-value">{videoVolume}%</span>
                </div>
              </div>
              <div className="video-container">
                {(() => {
                  const playlist = getVideoPlaylist();
                  if (playlist) {
                    return (
                      <iframe
                        src={`https://www.youtube.com/embed/${playlist.firstVideoId}?autoplay=1&loop=1&playlist=${playlist.playlistIds}&controls=0&modestbranding=1&rel=0&showinfo=0&enablejsapi=1`}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        onLoad={handleVideoLoad}
                        title="Playlist Informativa"
                      />
                    );
                  } else {
                    return (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        background: '#f7fafc',
                        color: '#a0aec0',
                        fontSize: '1rem',
                        textAlign: 'center',
                        padding: '2rem'
                      }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📺</div>
                        {currentUser && currentUser.role === 'administrator' ? 
                          'Configure vídeos clicando no ícone 🎥 no menu superior' : 
                          'Nenhum vídeo configurado'
                        }
                      </div>
                    );
                  }
                })()}
              </div>
            </div>

            {/* Painel Lateral - Histórico */}
            <div className="side-panel">
              <h3>Últimos Chamados</h3>
              
              <div className="history-list">
                {historico.length > 0 ? (
                  historico.slice(0, 3).map((item) => (
                    <div 
                      key={item.co_seq_atend} 
                      className="history-item"
                      style={{
                        backgroundColor: getRiskClassificationColor(item.classificacao_risco),
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <div className="history-line">
                        <span className="patient-name-hist">{item.no_cidadao}</span>
                        <span className="call-time">{formatDateTime(item.dt_ultima_alteracao_status)}</span>
                        <span className="professional">
                          {item.servico || '-'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', color: '#a0aec0', fontSize: '0.875rem' }}>
                    Nenhum histórico disponível
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;